<template>
  <div class="content">
    <server-error v-bind:error="error.serverError"></server-error>
    <!-- Profile -->
    <h4 v-if="showEditProfile() || showChangeSignature()" class="font-light">
      Profile
    </h4>
    <div v-if="showEditProfile()" class="u-box">
      <h6 class="toast__title">Missing Profile Details</h6>
      <div class="row">
        <div class="col-8 ignore-screen">
          You don't seem to have any user details set. Click the button to setup
          your user profile.
        </div>
        <div class="col-4 ignore-screen">
          <button class="btn btn-info outline u-pull-right">
            <router-link
              v-if="userProfile"
              :to="getUserChangeLink()"
              style="white-space: nowrap;"
            >
              <i
                class="fa fa-wrapper fa-user-cog pad-right"
                aria-hidden="true"
              ></i>
              Edit Profile
            </router-link>
          </button>
        </div>
      </div>
    </div>
    <div v-if="userProfile.profile && showChangeSignature()" class="u-box">
      <h6 class="toast__title">Signature Needed</h6>
      <div class="row">
        <div class="col-8 ignore-screen">
          You don't seem to have a <i>signature</i> setup yet. This is different
          to your password and is used to sign training records and exams. Click
          the button to setup your signature.
        </div>
        <div class="col-4 ignore-screen">
          <button class="btn btn-info outline u-pull-right">
            <router-link
              v-if="userProfile"
              :to="{ name: 'change-signature' }"
              style="white-space: nowrap;"
            >
              <i
                class="fa fa-wrapper fa-unlock pad-right"
                aria-hidden="true"
              ></i>
              Set Signature
            </router-link>
          </button>
        </div>
      </div>
    </div>
    <!-- Records -->
    <space></space>
    <h4 class="font-light">Records</h4>
    <placeholder
      v-if="permissions.includes('records.add_record') && loading.openRecords"
      title="Open Records"
    ></placeholder>
    <div
      v-if="permissions.includes('records.add_record') && !loading.openRecords"
    >
      <h6 class="font-alt font-light">Open Records</h6>
      <div v-if="openRecords.length == 0">You have no open records.</div>
      <record-list
        v-if="openRecords.length > 0"
        v-bind:records="openRecords"
      ></record-list>
    </div>

    <placeholder v-if="loading.ownRecords" title="My Records"></placeholder>
    <div v-if="!loading.ownRecords" class="section">
      <h6 class="font-alt font-light">My Records</h6>
      <div v-if="ownRecords.length == 0">
        You don't have any records yet.
      </div>
      <record-list
        v-if="ownRecords.length > 0"
        v-bind:records="ownRecords"
      ></record-list>
    </div>

    <!-- Exams -->
    <space></space>
    <h4 class="font-light">Exams</h4>
    <placeholder
      v-if="permissions.includes('exams.view_exam') && loading.openExams"
      title="Open Exams"
    ></placeholder>
    <div v-if="permissions.includes('exams.view_exam') && !loading.openExams">
      <h6 class="font-alt font-light">Open Exams</h6>
      <div v-if="openExams.length == 0">
        You have no open exams.
      </div>
      <exam-list
        v-if="openExams.length > 0"
        v-bind:exams="openExams"
      ></exam-list>
    </div>
    <div
      class="divider"
      v-if="permissions.includes('exams.view_exam') && openExams.length == 0"
    ></div>
    <placeholder v-if="loading.examList" title="My Exams"></placeholder>
    <div v-if="!loading.examList" class="section">
      <h6 class="font-alt font-light">My Exams</h6>
      <div v-if="examList.length == 0">You don't seem to have any exams.</div>
      <exam-list v-if="examList.length > 0" v-bind:exams="exams"></exam-list>
    </div>
  </div>
</template>
<style>
.tag--repeat {
  border: solid #999 1pt;
}
.tile .tile__title {
  line-height: 2.5rem;
}
.tile {
  border-bottom: solid #dedede 1pt;
  padding: 0.5em 2em;
}
</style>
<script>
import Placeholder from "@/components/Placeholder";
import RecordList from "@/components/records/RecordList";
import ExamList from "@/components/exams/ExamList";
import ServerError from "@/components/ServerError";

import { mapGetters } from "vuex";

export default {
  name: "dashboard",
  components: {
    ServerError,
    Placeholder,
    RecordList,
    ExamList
  },
  data: function() {
    return {};
  },
  computed: {
    results() {
      return this.$store.state.core.results;
    },
    resources() {
      return this.$store.state.core.resources;
    },
    openExams() {
      return this.$store.state.exams.openExams;
    },
    exams() {
      return this.$store.state.exams.examList;
    },
    ...mapGetters("auth", ["userProfile", "permissions"]),
    ...mapGetters("core", ["error"]),
    ...mapGetters("records", ["loading", "ownRecords", "openRecords"]),
    ...mapGetters("exams", ["examList", "loading"])
  },
  methods: {
    getData() {
      if (this.userProfile.id) {
        // Get records
        if (this.permissions.includes("records.add_record")) {
          this.$store.dispatch("records/getOpenRecords");
        }
        this.$store.dispatch("records/getOwnRecords");
        // Get exams
        this.$store.dispatch("exams/getExamDashboard");
        // Filter the exams to show only the candidates own exams
        this.$store.dispatch("exams/getExamList", {
          search: this.userProfile.arn, is_active: true,
        });
      }
    },
    showEditProfile() {
      return (
        this.userProfile.first_name == "" &&
        this.userProfile.last_name == "" &&
        this.userProfile.arn == ""
      );
    },
    showChangeSignature() {
      if (this.userProfile.profile) {
        return !this.userProfile.profile.has_signature;
      }
    },
    getUserChangeLink() {
      let userId = this.userProfile.id;
      if (!userId) {
        userId = "#";
      }
      let data = {
        name: "user-change",
        params: { userId: userId }
      };
      return data;
    }
  },
  watch: {
    userProfile() {
      this.getData();
    }
  },
  mounted() {
    if (!this.$store.getters["auth/userProfile"]) {
      this.$store.commit("records/setLoadingFlag", true);
    }
    if (this.userProfile.id) {
      this.getData();
    }
    this.$store.commit("core/clearErrors");
  }
};
</script>
